import { useState, useEffect, useCallback } from 'react';
import { MessageCircle, Copy, Share2, SkipForward, X, Check } from 'lucide-react';
import { toast } from 'sonner';

// Envoi guidé : enchaîne les clients un par un (affiche copiée dans le
// presse-papiers ou partagée, message pré-rempli, bouton WhatsApp, Suivant).
export const GuidedSendModal = ({ customers, campaign, posterBlob, posterUrl, waUrl, onMarkSent, onClose }) => {
  const [idx, setIdx] = useState(0);
  const [copied, setCopied] = useState(false);
  const current = customers[idx];
  const canShareFiles = typeof navigator !== 'undefined' && !!navigator.canShare && !!posterBlob
    && navigator.canShare({ files: [new File([posterBlob], 'affiche.png', { type: 'image/png' })] });

  const copyPoster = useCallback(async () => {
    if (!posterBlob || !navigator.clipboard?.write || typeof window.ClipboardItem === 'undefined') return false;
    try {
      await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': posterBlob })]);
      setCopied(true);
      return true;
    } catch {
      return false;
    }
  }, [posterBlob]);

  useEffect(() => { setCopied(false); copyPoster(); }, [idx, copyPoster]);

  if (!current) return null;

  const share = async () => {
    try {
      await navigator.share({
        files: [new File([posterBlob], 'affiche-nouveautes.png', { type: 'image/png' })],
        text: `Bonjour ${current.name?.split(' ')[0] || ''} 👋\n\n${campaign.message}`,
      });
      onMarkSent(current, true);
    } catch { /* annulé */ }
  };

  const openWhatsApp = () => {
    window.open(waUrl(current), '_blank', 'noopener');
    onMarkSent(current, true);
  };

  const next = () => {
    if (idx + 1 >= customers.length) { toast.success('Envoi guidé terminé'); onClose(); return; }
    setIdx(idx + 1);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" data-testid="guided-send-modal">
      <div className="bg-[color:var(--color-surface)] border border-[color:rgb(var(--primary-rgb)/0.40)] w-full max-w-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[color:var(--color-primary)] text-xs uppercase tracking-widest">Envoi guidé WhatsApp</p>
            <p className="text-zinc-400 text-sm" data-testid="guided-progress">Client {idx + 1} / {customers.length}</p>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="guided-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="h-1 bg-white/10 mb-5"><div className="h-1 bg-[color:var(--color-primary)] transition-all" style={{ width: `${((idx) / customers.length) * 100}%` }} /></div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <div className="bg-black aspect-[9/16] sm:aspect-auto sm:h-64 overflow-hidden">
            {posterUrl && <img src={posterUrl} alt="Affiche" className="w-full h-full object-contain" />}
          </div>
          <div className="sm:col-span-2 space-y-4">
            <div>
              <p className="text-white text-xl" style={{ fontFamily: 'Cormorant Garamond, serif' }} data-testid="guided-customer-name">{current.name}</p>
              <p className="text-zinc-500 text-sm">{current.phone}</p>
            </div>
            <ol className="text-zinc-400 text-sm space-y-1.5 list-decimal list-inside">
              <li>
                {copied ? <span className="text-emerald-400 inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Affiche copiée dans le presse-papiers</span>
                        : 'Copiez l\u2019affiche (ou partagez-la sur mobile)'}
              </li>
              <li>Ouvrez WhatsApp : le message est pré-rempli, collez l'affiche (Ctrl+V / coller) et envoyez</li>
              <li>Revenez ici et passez au client suivant</li>
            </ol>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={async () => { (await copyPoster()) ? toast.success('Affiche copiée') : toast.error('Copie impossible — utilisez Partager ou téléchargez l\u2019affiche'); }}
                className="inline-flex items-center gap-2 border border-white/20 text-white text-xs px-3 py-2 hover:border-[color:var(--color-primary)]" data-testid="guided-copy-btn">
                <Copy className="w-3.5 h-3.5" /> Copier l'affiche
              </button>
              {canShareFiles && (
                <button type="button" onClick={share}
                  className="inline-flex items-center gap-2 border border-[color:rgb(var(--primary-rgb)/0.50)] text-[color:var(--color-primary)] text-xs px-3 py-2 hover:bg-[color:var(--color-primary)] hover:text-black" data-testid="guided-share-btn">
                  <Share2 className="w-3.5 h-3.5" /> Partager l'affiche
                </button>
              )}
              <button type="button" onClick={openWhatsApp}
                className="inline-flex items-center gap-2 bg-[#25D366] text-white text-xs px-4 py-2 hover:bg-[#20BD5A]" data-testid="guided-whatsapp-btn">
                <MessageCircle className="w-3.5 h-3.5" /> Ouvrir WhatsApp
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/10">
          <button type="button" onClick={next} className="text-zinc-500 hover:text-white text-sm inline-flex items-center gap-1" data-testid="guided-skip-btn">
            <SkipForward className="w-4 h-4" /> Passer ce client
          </button>
          <button type="button" onClick={next} className="bg-[color:var(--color-primary)] text-black text-sm px-5 py-2 hover:bg-[color:var(--color-primary-hover)]" data-testid="guided-next-btn">
            {idx + 1 >= customers.length ? 'Terminer' : 'Client suivant →'}
          </button>
        </div>
      </div>
    </div>
  );
};
